import { useCallback, useMemo } from 'react';
import { useAuth } from '../auth/useAuth';
import { config } from '../config';

const API_BASE = config.apiBaseUrl;

/**
 * Client-portal API hook. MSAL-aware (token via useAuth.getAccessToken),
 * 30s timeout, JSON-by-default, RFC 7807 ApiError on non-2xx responses.
 *
 * Mirrors the admin-portal pattern at apps/admin-portal/src/hooks/useApi.ts —
 * shared shape so engineers moving between portals see the same API.
 *
 * Usage:
 *   const { get, post, put, patch, del, downloadBlob } = useApi();
 *   const cases = await get<MyCasesResponse>('/api/v1/portal/my-cases');
 */
export function useApi() {
  const { getAccessToken } = useAuth();

  const apiFetch = useCallback(async (path: string, options?: RequestInit & { externalSignal?: AbortSignal }) => {
    const token = await getAccessToken();
    // Combine the caller's optional cancellation signal with our own 30s timeout.
    // Whichever fires first aborts the underlying fetch — fixes ERR_INSUFFICIENT_RESOURCES
    // when callers stack requests on rapid navigation (mirrors admin-portal #7040 fix).
    const timeoutController = new AbortController();
    const timeout = setTimeout(() => timeoutController.abort(), 30000);
    const signals: AbortSignal[] = [timeoutController.signal];
    if (options?.externalSignal) signals.push(options.externalSignal);
    const signal = signals.length > 1 ? AbortSignal.any(signals) : signals[0];
    try {
      const { externalSignal: _drop, ...rest } = options ?? {};
      void _drop;
      const res = await fetch(`${API_BASE}${path}`, {
        ...rest,
        signal,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...options?.headers,
        },
      });
      return res;
    } finally {
      clearTimeout(timeout);
    }
  }, [getAccessToken]);

  const get = useCallback(async <T>(path: string, signal?: AbortSignal): Promise<T> => {
    const res = await apiFetch(path, { externalSignal: signal });
    if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
    return res.json();
  }, [apiFetch]);

  const post = useCallback(async <T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> => {
    const res = await apiFetch(path, { method: 'POST', body: JSON.stringify(body), externalSignal: signal });
    if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
    // 204 No Content has no JSON body
    if (res.status === 204) return undefined as T;
    return res.json();
  }, [apiFetch]);

  const put = useCallback(async <T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> => {
    const res = await apiFetch(path, { method: 'PUT', body: JSON.stringify(body), externalSignal: signal });
    if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
    if (res.status === 204) return undefined as T;
    return res.json();
  }, [apiFetch]);

  /**
   * PATCH — a PARTIAL update, distinct from `put` and not interchangeable with it: the routes that
   * declare [HttpPatch] answer 405 to anything else. Added for the basket rename (#16317), whose
   * body is `{ name, rowVersion }` rather than a whole basket. Mirrors `put` in every other respect,
   * 204 included, so a caller moving between the two sees one shape.
   */
  const patch = useCallback(async <T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> => {
    const res = await apiFetch(path, { method: 'PATCH', body: JSON.stringify(body), externalSignal: signal });
    if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
    if (res.status === 204) return undefined as T;
    return res.json();
  }, [apiFetch]);

  const del = useCallback(async (path: string, signal?: AbortSignal): Promise<void> => {
    const res = await apiFetch(path, { method: 'DELETE', externalSignal: signal });
    if (!res.ok && res.status !== 204) throw new ApiError(res.status, await res.json().catch(() => ({})));
  }, [apiFetch]);

  const downloadBlob = useCallback(async (path: string, fileName: string): Promise<void> => {
    const res = await apiFetch(path);
    if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      URL.revokeObjectURL(url);
    }
  }, [apiFetch]);

  // Stable object reference. Without useMemo this hook returns a fresh object literal
  // every render — pages doing `const api = useApi(); ... useEffect(()=>fetch, [api])`
  // re-fire their effect on every render and stack requests until Chrome's connection
  // pool exhausts (ERR_INSUFFICIENT_RESOURCES — root cause discovered 2026-05-08).
  return useMemo(() => ({ get, post, put, patch, del, downloadBlob }), [get, post, put, patch, del, downloadBlob]);
}

/**
 * Thrown for any non-2xx response. The body conforms to RFC 7807 Problem
 * Details — `detail`, `title`, `status`, `traceId`, optionally `errors` for
 * validation failures.
 *
 * Pattern:
 *   try { await get<X>('/api/v1/...'); }
 *   catch (e) {
 *     if (e instanceof ApiError && e.status === 404) showEmpty();
 *     else if (e instanceof ApiError && e.status === 403) showForbidden();
 *     else throw e;
 *   }
 */
export class ApiError extends Error {
  constructor(public status: number, public body: Record<string, unknown>) {
    super((body?.detail as string) || `API error ${status}`);
  }
}
